import type { ZyDocumentAttributes } from '@/components/tiptap-editor/tiptap-extension/zydocument-extension';
import { getMaImagesLibraryBaseUrl } from '@/lib/ma-images-library';
import type { ZybookTestQuestion } from '@/lib/zybooks-parser/from-test-question';
import type { ConvertProseMirrorToZybooksJSONOptions } from '@/lib/zybooks-parser/to-zybooks-json-core';
import type { JSONContent } from '@tiptap/core';
import { browserServers, itLabsServer, zyAuthor, zyServer, zyServer2 } from './servers';
import { MAX_FILE_SIZE } from './utils';
import { createZybooksApi, type S3Object, type ZybooksApi } from './zybooks-api-core';

export type { S3Object } from './zybooks-api-core';

// Resolve browserServers per call so loading this module never touches it (Lambda stub, test mocks).
const browserApi = (): ZybooksApi => createZybooksApi(browserServers);

/**
 * Content resource as returned by zyserver's section endpoint
 * Note: Field names differ from FullBookContentResource (e.g., 'id' vs 'content_resource_id')
 */
export interface ZyServerContentResource {
    id: number;
    caption: string | null;
    payload: Record<string, unknown>;
    instructions: string | null;
    parts: number;
    width: number | null;
    height: number | null;
    guid: string;
    gdoc_id: string | null;
    parent_gdoc_id: string | null;
    activity_type: string | null;
    type: string; // This is 'resource_type' in our FullBook interface
    release_id: number;
    optional: boolean;
    property_overrides: string | null;
}

/**
 * Section as returned by zyserver's section endpoint
 */
export interface ZyServerSection {
    content_resources: ZyServerContentResource[];
    canonical_section_id: number;
    subject_id: number;
    release_id: number;
    canonical_chapter_number: number;
    canonical_section_number: number;
    title: string;
    short_title: string | null;
    custom_title: string | null;
    summary: string;
    type: string;
    guid: string | null;
    gdoc_id: string | null;
    parent_gdoc_id: string | null;
    zybooks_owned: boolean;
    hidden: boolean;
    is_timegated: boolean;
    optional: boolean;
    new: boolean;
}

/**
 * Response from zyserver's GET /v1/zybook/:code/chapter/:n/section/:n
 */
export interface ZyServerSectionResponse {
    success: boolean;
    section: ZyServerSection;
}

/**
 * Response from zyserver's GET /v1/zybook/:code/about
 */
export interface GetZybookAboutPagesResponse {
    success: boolean;
    about_pages: ZyServerSection[];
}

export interface ZyServerTestQuestion {
    test_question_id: number;
    payload: ZybookTestQuestion['payload'];
    canonical_section_id: number;
    number: number;
    guid: string;
    gdoc_id: string | null;
    subject_id: number;
    release_id: number;
    show_in_eval: boolean;
    dev_book: string | null;
    gdoc_parent_folder: string | null;
    chapter_number?: number;
    section_number?: number;
}

export interface GetZybookTestQuestionsResponse {
    success: boolean;
    test_questions: ZyServerTestQuestion[];
    total_test_question_count?: number;
}

export interface GetDocumentZyVersionsResponse {
    success: boolean;
    versions: string[];
}

export interface GetSameContentSourceTestQuestionsResponse {
    success: boolean;
    test_questions: Array<
        ZyServerTestQuestion & {
            chapter_number: number;
            section_number: number;
            section_guid: string;
            version: string | null;
        }
    >;
    same_content_source_subject_count: number;
}

export type ZyServerSameContentSourceTestQuestion = GetSameContentSourceTestQuestionsResponse['test_questions'][number];

export interface CreateItLabInstancesResponse {
    success?: boolean;
    streaming_urls?: string[];
    ec2_instance_ids?: string[];
    created_security_group?: string;
    different_crids_with_running_vm?: number[];
}

export interface DeleteItLabInstancesResponse {
    success?: boolean;
}

export interface ItLabImage {
    itlab_image_id: number;
    image_name: string;
    ami_id: string;
    description?: string | null;
}

export interface GetItLabImagesResponse {
    success: boolean;
    itlab_images: {
        production: ItLabImage[];
        development: ItLabImage[];
    };
}

/**
 * Section metadata within a chapter (from /v1/zybooks endpoint, no content_resources)
 */
export interface ZyServerBookSection {
    number: number;
    short_title: string;
    title: string;
    canonical_chapter_number: number;
    canonical_section_number: number;
    canonical_section_id: number;
    hidden: boolean;
    optional: boolean;
    type: string;
    guid: string;
    gdoc_id: string | null;
    parent_gdoc_id: string | null;
    metadata?: {
        hasLabs?: boolean;
    };
}

/**
 * Chapter within a zybook (from /v1/zybooks endpoint)
 */
export interface ZyServerBookChapter {
    number: number;
    title: string;
    sections: ZyServerBookSection[];
}

/**
 * Zybook metadata as returned by /v1/zybooks endpoint
 */
export interface ZyServerBook {
    zybook_id: number;
    zybook_code: string;
    title: string;
    development: boolean;
    chapters: ZyServerBookChapter[];
    unused: ZyServerBookSection[];
    subjects: ZyServerSubject[];
}

export interface ZyServerSubject {
    subject_id: number;
    release_id: number;
    name: string;
    display_name: string;
    version: string;
    dev_book: string;
    lab_dev_book: string;
    content_source: string;
    lab_language: string;
    subject_releases: {
        number: number;
        release_id: number;
        status: string;
    }[];
}

export interface GetConvertedGdocJsonOptions {
    forceNoContentVersion?: boolean;
    returnContents?: boolean;
    contentVersion?: string;
}

export interface GetConvertedGdocJsonResponse {
    error: {
        code: number;
        message: string;
    };
    errors: Record<string, any>[];
    sections: Record<string, any>[];
    chapter_title: string;
    short_chapter_title: string | null;
    chapter_number: number;
    contents: string | null;
}

/**
 * Response from zyserver's GET /v1/zybooks?zybooks=["code"]
 */
export interface ZyServerBooksResponse {
    success: boolean;
    zybooks: ZyServerBook[];
}

export interface AcademicTerm {
    academic_term_id: number;
    name: string;
    year: number;
}

export async function getAcademicTerms(): Promise<AcademicTerm[]> {
    const response = await zyServer.get<{ success: boolean; academic_terms: AcademicTerm[] }>('academic_terms');
    return response.academic_terms;
}

export interface FullBookContentResource {
    content_resource_id: number;
    resource_type: string | null;
    activity_type: string | null;
    caption: string | null;
    instructions: string | null;
    payload: string;
    parts: number | null;
    height: number | null;
    width: number | null;
    guid: string | null;
    gdoc_id: string | null;
    parent_gdoc_id: string | null;
    creation_date: string | null;
    last_updated: string | null;
    num_updates: number | null;
}

export interface FullBookSection {
    number: number;
    canonical_section_id: number;
    title: string;
    short_title: string | null;
    guid: string | null;
    content_resources: FullBookContentResource[];
}

export interface FullBookChapter {
    number: number;
    title: string;
    short_title: string | null;
    sections: FullBookSection[];
}

export interface FullBook {
    zybook_code: string;
    zybook_id: number;
    chapters: FullBookChapter[];
    unused: FullBookSection[];
}

export interface ZyXmlToJsonResponse {
    success: boolean;
    location?: string;
    state?: 'SUCCESS' | 'PENDING' | 'RETRY';
    error?: {
        message: string;
    };
    [key: string]: any; // For additional fields in the result
}

export interface ContentResourceMetaData {
    content_resource_id: number;
    chapter_number: number;
    section_number: number;
    number: string | number; // Can be "1.3.1" or just 1
    caption: string | null;
    name: string | null;
}

export interface SubRefMetaData {
    [subRefGuid: string]: string;
}

export interface SectionRefMetaData {
    chapter_number: number;
    section_number: number;
    guid: string;
    section_title: string;
}

export interface InlineEnumMetaData {
    chapter_number: number;
    section_number: number;
    letter: string;
}

export interface ReferenceLetteringMap {
    // Maps sub-ref GUID to enum ID
    // Example: { "bfdadbc5-1414-4d32-b8cc-cfe6ac2f82f6": "3a58f0c5-7fe4-4f50-a2da-f03cc3440c11" }
    [subRefGuid: string]: string;
}

export interface InlineEnumsLetteringMap {
    // Nested structure: chapter -> section -> enumId -> letter
    // Example: { "1": { "1": { "b8f2d4a8-0d2b-4c4c-8ea6-a35fcc95e872": "a", ... } } }
    [chapterNumber: string]: {
        [sectionNumber: string]: {
            [enumId: string]: string;
        };
    };
}

export interface ReferenceToContentResources {
    cr_map: Record<string, ContentResourceMetaData>;
    sub_refs_map: Record<string, string>;
    section_refs_map: Record<string, SectionRefMetaData>;
    inline_enums_lettering_map: InlineEnumsLetteringMap;
    inline_enum_map: Record<string, InlineEnumMetaData>;
    reference_lettering_map: ReferenceLetteringMap;
}

const defaultReferenceMetaData: ReferenceToContentResources = {
    cr_map: {},
    sub_refs_map: {},
    section_refs_map: {},
    inline_enums_lettering_map: {},
    inline_enum_map: {},
    reference_lettering_map: {},
};

export interface ZyStudioResourceTokenParams {
    access: string;
    actor: string;
    content_resource_id: number;
    frame_id: string;
    guid: string;
    kinds: string;
    release_id: number;
    subject_id: string;
    zybook_id: string;
}

export interface MaDocumentImportPayload {
    document_name: string;
    tiptap_json: JSONContent;
    chapter_number: number;
    section_number: number;
    chapter_title: string;
    short_chapter_title: string | null;
    section_title: string;
    short_section_title: string | null;
    section_guid: string;
    zybook_code: string;
    release_id: number | null;
    type: MaDocumentType;
    status: MaDocumentStatus;
    link_section_ma_document_id?: number;
}

export type MaDocumentStatus = 'dev' | 'released' | 'draft';

export type MaDocumentType = 'lab' | 'section' | 'test_question' | 'itlab';

export interface ReferenceSource {
    ma_reference_id?: number | null;
    section_guid: string | null;
    node_id: string | null;
    chapter_number?: number | null;
    section_number?: number | null;
    ma_document_id?: number | null;
}

export interface ReferenceDestination {
    section_guid: string | null;
    node_id: string | null;
    sub_node_id?: string | null;
    chapter_number?: number | null;
    section_number?: number | null;
    ma_document_id?: number | null;
    resource_number?: number | null;
}

export interface Reference {
    source: ReferenceSource;
    destination: ReferenceDestination;
}

export interface MaZybookDocument {
    ma_zybook_document_id: number;
    zybook_id: number;
    ma_document_id: number;
    ma_test_question_document_ids: number[];
    chapter_number: number;
    section_number: number;
    ma_zybook_chapter_id: number;
    section_title: string;
    short_section_title: string | null;
}

export interface MaTestQuestionDocumentLink {
    test_question_document_id: number;
    test_question_document_name: string;
    placement?: 'included' | 'excluded';
}

export interface MaDocument {
    document_id: number;
    document_name: string;
    section_guid: string;
    release_id: number | null;
    gdoc_id: string | null;
    status: MaDocumentStatus | null;
    type: MaDocumentType | null;
}

export interface GetMaDocumentsResponse {
    document_id: number;
    document_name: string;
    section_guid: string;
    section_title: string;
    release_id: number | null;
    test_question_documents: MaTestQuestionDocumentLink[];
    zybook_code: string;
    chapter_number: number;
    section_number: number;
    placement?: 'included' | 'excluded' | 'unplaced';
    status?: MaDocumentStatus | null;
    type?: MaDocumentType | null;
    references: Reference[] | null;
    referenced: Reference[] | null;
}

export interface MaDocumentImportLog {
    ma_document_import_log_id?: number;
    zybook_code: string;
    chapter_number: number;
    section_number: number;
    section_guid: string;
    release_id: number | null;
    gdoc_id: string | null;
    document_name: string | null;
    status: string;
    status_message?: string | null;
    created_at?: string;
}

/** Status a work session is started with. */
export type MaDocumentWorkStatusValue =
    'Draft1Doing' | 'Rev1Doing' | 'Rev1Fixes' | 'CopyEditDoing' | 'Rev2Doing' | 'Rev2Fixes' | 'BugFixes';

/** Status a work session is ended with: its start status when the work is not finished, or its Done status. */
export type MaDocumentWorkEndStatusValue = MaDocumentWorkStatusValue | 'Draft1Done' | 'Rev1Done' | 'CopyEditDone' | 'Rev2Done';

export interface MaDocumentWorkStatus {
    ma_document_work_status_id: number;
    user_id: number;
    ma_document_id: number;
    start_status: MaDocumentWorkStatusValue;
    /** Null while the session is open. */
    end_status: MaDocumentWorkEndStatusValue | null;
    remark: string | null;
    start_at: string;
    end_at: string | null;
    work_duration: number | null;
    first_name?: string;
    last_name?: string;
}

export interface GetMaDocumentMaZybookDocumentRow extends MaZybookDocument {
    chapter_title: string | null;
    short_chapter_title: string | null;
    zybook_code: string;
    references: Reference[] | null;
    referenced: Reference[] | null;
}

export interface GetMaDocumentResponse {
    ma_document_id: number;
    document_name: string;
    section_guid: string;
    release_id: number | null;
    gdoc_id: string | null;
    type: MaDocumentType | null;
    status?: MaDocumentStatus | null;
    ma_zybook_documents: GetMaDocumentMaZybookDocumentRow[];
}

export interface CreateDevZybookResponse {
    success: boolean;
    zybook: {
        zybook_id: number;
        zybook_code: string;
        development: boolean;
    };
}

export interface SubjectDevZybook {
    zybook_id: number;
    zybook_code: string;
    title: string;
    development: boolean;
}

export interface GetSubjectDevZybooksResponse {
    success: boolean;
    zybooks: SubjectDevZybook[];
}

export interface MaImage {
    ma_image_id: number;
    image_uuid: string;
    filename: string;
    alt_text: string | null;
    created_at: string;
    user_id: number | null;
    s3_path: string;
    s3_key: string;
}

export interface MaImageDocument {
    ma_image_node_uuid: string;
    ma_image_document_id: number;
    ma_image_id: number;
    ma_document_id: number;
}

export interface MaImageUsage {
    ma_document_id: number;
    document_name: string;
    document_type: string | null;
    zybook_code: string | null;
    chapter_number: number | null;
    section_number: number | null;
    section_title: string | null;
}

export interface GetMaImageUsagesResponse {
    success: boolean;
    usages: MaImageUsage[];
    error?: string;
}

export interface GetMaImagesResponse {
    success: boolean;
    ma_images: MaImage[];
    total_count: number;
    limit: number;
    offset: number;
    error?: string;
}

export const IMAGE_LIBRARY_UNUSED_SUBJECT = 'Unused';

export interface GetMaImageLibrarySubjectsResponse {
    success: boolean;
    subjects: string[];
    error?: string;
}

export interface UploadedImageResponse {
    success: boolean;
    ma_image: MaImage;
    ma_image_document: MaImageDocument | null;
    error?: string;
}

export interface LinkImageResponse {
    success: boolean;
    ma_image_document: MaImageDocument;
    error?: string;
}

export interface RegisteredDocumentImage {
    image_uuid: string;
    ma_image_id: number;
    ma_image_node_uuid: string;
}
export interface MaSubjectSummary {
    subject_id: number;
    name: string;
    cover_color: string | null;
}

export interface MaSubjectBook {
    zybook_id: number;
    zybook_code: string;
    title: string;
    development: boolean;
    is_ma_zybook: boolean;
    variant_id: string;
}

export interface MaSubjectBookWithoutVariant {
    zybook_id: number;
    zybook_code: string;
    title: string;
    development: boolean;
    is_ma_zybook: boolean;
}

export interface MaSubjectBookVariant {
    id: string;
    label: string;
}

export interface MaSubjectReleaseRow {
    release_number: number;
    release_id: number;
    status: string;
    books_by_variant_id: Record<string, MaSubjectBookWithoutVariant>;
}

export interface GetSubjectBooksResponse {
    success: boolean;
    subject: {
        subject_id: number;
        name: string;
        display_name: string;
        cover_color: string | null;
    };
    variants: MaSubjectBookVariant[];
    dev_books: MaSubjectBook[];
    release_rows: MaSubjectReleaseRow[];
}

export type GetUserByIdsResponse = {
    success: boolean;
    users: {
        user_id: number;
        first_name: string;
        last_name: string;
    }[];
};

export interface UpsertItLabContentResourceResponse {
    success: boolean;
    content_resource_id: number;
}

/**
 * Converts zyXML to JSON format
 * @param xml - The zyXML content to convert
 * @returns The converted JSON result
 */
export const zyxmlToJson = async (xml: string): Promise<ZyXmlToJsonResponse> => {
    return await zyAuthor.post('zyXml_to_json', { body: { zyXml: xml } });
};

function buildPreviewAboutPage(contentResources: any[], previewTitle: string, previewSectionNumber: number, previewDevBook: string) {
    return {
        content_resources: contentResources,
        canonical_section_id: 1,
        title: previewTitle,
        short_title: previewTitle,
        dev_book: previewDevBook,
        canonical_zybook_id: 275967,
        subject_id: 44,
        release_id: 70,
        canonical_chapter_number: 0,
        canonical_section_number: previewSectionNumber,
        custom_title: null,
        summary: '',
        type: 'section',
        guid: null,
        gdoc_id: null,
        parent_gdoc_id: null,
        zybooks_owned: true,
        is_draft: false,
        hidden: false,
        is_timegated: false,
        optional: false,
        new: false,
        do_not_anchor: null,
        clone_base_canonical_section_id: null,
        original_canonical_section_id: null,
        author_user_id: null,
        subject_label: '',
        lab_dev_book: '',
        version: '',
        metadata: '{}',
        zybook_content_sections_metadata: {},
        subject_name: '',
        current_server_time: '',
        timegates: [],
        durations: [],
        modernAuthoringLivePreview: true,
    };
}

function buildPreviewTocSection(previewTitle: string, previewChapterNumber: number, previewSectionNumber: number, previewDevBook: string) {
    return {
        number: previewSectionNumber,
        short_title: previewTitle,
        title: previewTitle,
        canonical_chapter_number: previewChapterNumber,
        canonical_section_number: previewSectionNumber,
        canonical_section_id: 1,
        dev_book: previewDevBook,
        release_id: 70,
        subject_id: 44,
        subject_label: '',
        subject_name: '',
        custom_title: null,
        guid: null,
        gdoc_id: null,
        parent_gdoc_id: null,
        zybooks_owned: true,
        metadata: '{}',
        lab_dev_book: null,
        version: null,
        optional: false,
        hidden: false,
        new: false,
        do_not_anchor: null,
        type: 'section',
        summary: '',
        is_timegated: false,
        uses_itlabs: 0,
        uses_zylabs: 0,
        zybook_content_sections_metadata: {},
    };
}

export const generatePreviewData = (
    contentResources: any[] = [],
    documentAttributes: Partial<ZyDocumentAttributes> = {},
    references: Partial<ReferenceToContentResources> = defaultReferenceMetaData,
    testQuestions?: ZybookTestQuestion[],
    previewVersion?: string,
    isAboutPage = false,
) => {
    const { title, chapterNumber, sectionNumber, documentType } = documentAttributes;
    const devBookName = documentAttributes.zybookCode;
    const selectedVersion = previewVersion ?? 'default';
    const previewTitle = title?.[selectedVersion] ?? title?.['default'] ?? 'Section Title Not Set';
    const previewChapterNumber = chapterNumber ?? 1;
    const previewSectionNumber = sectionNumber ?? 1;
    const previewDevBook = devBookName?.trim() || '';
    const previewChapters = [
        {
            number: previewChapterNumber,
            title: previewTitle,
            sections: [buildPreviewTocSection(previewTitle, previewChapterNumber, previewSectionNumber, previewDevBook)],
        },
    ];

    const aboutPages: ZyServerSection[] = isAboutPage
        ? [buildPreviewAboutPage(contentResources, previewTitle, previewSectionNumber, previewDevBook)]
        : [];
    const sectionContentResources = isAboutPage ? [] : contentResources;

    return {
        sectionResponse: {
            success: true,
            section: {
                content_resources: sectionContentResources,
                canonical_section_id: 1,
                title: previewTitle,
                short_title: previewTitle,
                dev_book: previewDevBook,
                canonical_zybook_id: 275967,
                subject_id: 44,
                release_id: 70,
                canonical_chapter_number: previewChapterNumber,
                canonical_section_number: previewSectionNumber,
                custom_title: null,
                summary: '',
                type: documentType === 'itlab' ? 'it-lab' : 'section',
                guid: null,
                gdoc_id: null,
                parent_gdoc_id: null,
                zybooks_owned: true,
                is_draft: false,
                hidden: false,
                is_timegated: false,
                optional: false,
                new: false,
                do_not_anchor: null,
                clone_base_canonical_section_id: null,
                original_canonical_section_id: null,
                author_user_id: null,
                subject_label: '',
                lab_dev_book: '',
                version: '',
                metadata: '{}',
                zybook_content_sections_metadata: {},
                subject_name: '',
                current_server_time: '',
                timegates: [],
                durations: [],
                modernAuthoringLivePreview: true,
            },
        },
        zybooksResponse: {
            success: true,
            zybooks_with_links: 0,
            lms_name: null,
            zybooks: [
                {
                    zybook_id: 275967,
                    has_test_questions: true,
                    zybook_code: previewDevBook,
                    title: previewDevBook,
                    snippet: '',
                    summary: '',
                    metadata: {},
                    development: true,
                    disable_extensions: true,
                    supports_access_keys: false,
                    time_zone: null,
                    zybook_isbn: null,
                    zylabs_enabled: false,
                    autosubscribe: false,
                    academic_term: {
                        academic_term_id: 1,
                        name: 'None',
                        year: 0,
                    },
                    chapters: previewChapters,
                    references_to_content_resources: references,
                    public: false,
                    is_master_lti_template: false,
                    is_test: false,
                    feature_flags: {
                        feature1: false,
                        feature6: false,
                        feature8: false,
                        feature9: false,
                    },
                    force_access_key: false,
                    found_via_access_key_lookup: false,
                    found_via_isbn_lookup: false,
                    has_assignments: false,
                    unused: [],
                    course: {
                        course_call_number: 'None',
                        course_id: 1,
                        institution: {
                            institution_id: 1,
                            institution_type: null,
                            metadata: {},
                            name: 'None',
                            num_enrolled_full_time: null,
                            salesforce_account_id: 'None',
                            system_term: null,
                        },
                        metadata: {},
                        name: 'None',
                        salesforce_course_id: 'None',
                    },
                    subjects: [
                        {
                            cover_color: 'lightblue',
                            display_name: 'Python 3',
                            header_icon: 'terminal',
                            ineligible_for_discount: false,
                            ineligible_for_extension: false,
                            label: 'Python 3',
                            name: 'Python 3',
                            release_id: 70,
                            subject_id: 44,
                            title: 'Programming in Python 3',
                            uses_itlabs: false,
                            uses_zylabs: true,
                        },
                    ],
                    subscription: {
                        begin_date: '2024-06-20T18:55:38.972835Z',
                        config: {},
                        end_date: '2024-06-20T18:55:38.972836Z',
                        permissions: {
                            add_remove_user: false,
                            edit_content: false,
                            modify_assignments: false,
                            modify_instructor_notes: false,
                            modify_perms: false,
                            modify_tests: false,
                            reorder_content: false,
                            view_content: false,
                            view_grades: false,
                            view_solutions: false,
                        },
                        user: {
                            admin: documentType === 'itlab',
                        },
                        subscribed: false,
                        subscription_id: null,
                        transactions: [],
                        unsubscribed: false,
                        user_info: null,
                        zybook_role: 'Unsubscribed',
                    },
                    subscription_info: {
                        additional_instructions: null,
                        additional_purchases_info: [],
                        begin_date: null,
                        cutoff_date: null,
                        duration: null,
                        end_date: null,
                        force_access_key: false,
                        price: 1000000,
                        renewal_discount: null,
                        supports_access_keys: false,
                        user_info: [],
                    },
                    about_pages: aboutPages,
                },
            ],
        },
        baseImageUrl: getMaImagesLibraryBaseUrl(),
        testQuestions,
    };
};

export const getModernAuthoringJsonFiles = async () => {
    const data = await zyAuthor.get('get_modern_authoring_json');
    return data;
};

export const COLORIZE_LANGUAGE_OPTIONS = [
    { value: '', label: 'Plain Text' },
    { value: 'c', label: 'C' },
    { value: 'cpp', label: 'C++' },
    { value: 'csharp', label: 'C#' },
    { value: 'java', label: 'Java' },
    { value: 'python', label: 'Python' },
    { value: 'javascript', label: 'JavaScript' },
    { value: 'typescript', label: 'TypeScript' },
    { value: 'html', label: 'HTML' },
    { value: 'css', label: 'CSS' },
    { value: 'sql', label: 'SQL' },
    { value: 'php', label: 'PHP' },
    { value: 'kotlin', label: 'Kotlin' },
    { value: 'matlab', label: 'MATLAB' },
    { value: 'verilog', label: 'Verilog' },
    { value: 'vhdl', label: 'VHDL' },
    { value: 'xml', label: 'XML' },
    { value: 'json', label: 'JSON' },
    { value: 'yaml', label: 'YAML' },
    { value: 'bash', label: 'Shell' },
    { value: 'asm', label: 'Assembly' },
];

export const colorizeCode: ZybooksApi['colorizeCode'] = (...args) => browserApi().colorizeCode(...args);

/**
 * Batch-resolve user ids to display names.
 * @param userIds - Array of user ids to lookup
 * @returns Map of user ids to display names
 */
export async function getUserByIds(userIds: string[]): Promise<Map<string, string>> {
    if (userIds.length === 0) {
        return new Map();
    }

    const data = await zyServer.post<GetUserByIdsResponse>('users/lookup', {
        body: { user_ids: userIds.map(Number) },
    });
    if (!data.success) {
        console.error('Failed to lookup users by ids');
        return new Map();
    }

    const result = new Map<string, string>();
    for (const user of data.users) {
        const name = `${user.first_name} ${user.last_name}`.trim();
        if (name) {
            result.set(String(user.user_id), name);
        }
    }
    return result;
}

/**
 * Gets or creates a zyStudio resource token for initializing CodingRooms workspace
 * @param params - Resource token parameters including access level, actor type, content resource details, and workspace identifiers
 * @returns Promise resolving to token and ACI token for CodingRooms authentication
 */
export const getOrCreateZyStudioResourceToken = async (
    params: ZyStudioResourceTokenParams,
): Promise<{ token: string; aci_token: string }> => {
    const resourceTokenResponse = await zyServer.get(`zystudio/resource_token`, { params });
    if (!resourceTokenResponse.success) {
        throw new Error('Failed to fetch zyStudio resource token');
    }
    return resourceTokenResponse;
};

/**
 * Maps a ZyServerContentResource to FullBookContentResource
 * Handles field name differences between zyserver response and our interface
 */
export function mapContentResource(cr: ZyServerContentResource): FullBookContentResource {
    return {
        content_resource_id: cr.id,
        resource_type: cr.type,
        activity_type: cr.activity_type,
        caption: cr.caption,
        instructions: cr.instructions,
        payload: JSON.stringify(cr.payload),
        parts: cr.parts,
        height: cr.height,
        width: cr.width,
        guid: cr.guid,
        gdoc_id: cr.gdoc_id,
        parent_gdoc_id: cr.parent_gdoc_id,
        creation_date: null,
        last_updated: null,
        num_updates: null,
    };
}

async function fetchSectionContent(zybookCode: string, chapterNumber: number, sectionNumber: number): Promise<ZyServerContentResource[]> {
    const response: ZyServerSectionResponse = await zyServer.get(
        `zybook/${encodeURIComponent(zybookCode)}/chapter/${chapterNumber}/section/${sectionNumber}`,
    );
    return response.section.content_resources;
}

export async function getSection(zybookCode: string, chapterNumber: number, sectionNumber: number): Promise<ZyServerSection> {
    const response: ZyServerSectionResponse = await zyServer.get(
        `zybook/${encodeURIComponent(zybookCode)}/chapter/${chapterNumber}/section/${sectionNumber}`,
    );
    return response.section;
}

export async function getZybookTestQuestions(zybookCode: string): Promise<ZyServerTestQuestion[]> {
    const response: GetZybookTestQuestionsResponse = await zyServer2.get(`zybook/${encodeURIComponent(zybookCode)}/test_questions`);
    return response.test_questions;
}

export const getZybook = async (zybookCode: string): Promise<ZyServerBook> => {
    const booksResponse: ZyServerBooksResponse = await zyServer.get(`zybooks?zybooks=${encodeURIComponent(JSON.stringify([zybookCode]))}`);
    if (!booksResponse.zybooks || booksResponse.zybooks.length === 0) {
        throw new Error(`No zybook found with code: ${zybookCode}`);
    }
    return booksResponse.zybooks[0];
};

export const getFullBook = async (zybookCode: string): Promise<FullBook> => {
    // Step 1: Fetch book structure (chapters and sections metadata)
    const zybook = await getZybook(zybookCode);

    // Step 2: Fetch content_resources for each section in parallel
    const chapters: FullBookChapter[] = await Promise.all(
        zybook.chapters.map(async chapter => {
            const sections: FullBookSection[] = await Promise.all(
                chapter.sections.map(async section => {
                    const contentResources = await fetchSectionContent(zybookCode, chapter.number, section.number);

                    return {
                        number: section.number,
                        canonical_section_id: section.canonical_section_id,
                        title: section.title,
                        short_title: section.short_title,
                        guid: null, // Section guid not available from this endpoint
                        content_resources: contentResources.map(mapContentResource),
                    };
                }),
            );

            return {
                number: chapter.number,
                title: chapter.title,
                short_title: null, // Chapter short_title not available from /v1/zybooks
                sections,
            };
        }),
    );

    // Step 3: Fetch unused sections
    const unusedSections: FullBookSection[] = await Promise.all(
        (zybook.unused || []).map(async section => {
            const contentResources = await fetchSectionContent(zybookCode, -1, section.number);

            return {
                number: section.number,
                canonical_section_id: section.canonical_section_id,
                title: section.title,
                short_title: section.short_title,
                guid: null,
                content_resources: contentResources.map(mapContentResource),
            };
        }),
    );

    return {
        zybook_code: zybook.zybook_code,
        zybook_id: zybook.zybook_id,
        chapters,
        unused: unusedSections,
    };
};

/**
 * Handles image upload to S3 with progress tracking
 * @param file The file to upload
 * @param documentId Optional MA document to link the image to
 * @param onProgress Optional callback for upload progress updates
 * @param signal Optional AbortSignal for cancelling the upload
 * @returns Promise resolving to the image key of the uploaded image
 */
export const uploadImageToS3 = async (
    file: File,
    documentId?: number,
    onProgress?: (event: { progress: number }) => void,
    signal?: AbortSignal,
): Promise<UploadedImageResponse> => {
    if (!file) {
        throw new Error('No file provided');
    }

    if (file.size > MAX_FILE_SIZE) {
        throw new Error(`File size exceeds maximum allowed (${MAX_FILE_SIZE / 1024} KB)`);
    }

    try {
        const formData = new FormData();
        formData.append('file', file);
        if (typeof documentId === 'number' && Number.isInteger(documentId) && documentId > 0) {
            formData.append('document_id', String(documentId));
        }

        const response = await zyAuthor.uploadWithProgress<UploadedImageResponse>(
            'modern_authoring/upload_image',
            formData,
            onProgress,
            signal,
        );

        if (!response.success) {
            throw new Error('Image upload failed', { cause: response.error });
        }
        return response;
    } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
            throw error;
        }
        throw new Error('Image upload failed', { cause: error });
    }
};

/**
 * Handles image deletion from S3
 * @param imageKey The key of the image to delete
 * @returns Promise resolving to a boolean indicating success
 */
export const deleteImageFromS3 = async (imageKey: string): Promise<boolean> => {
    try {
        const response = await zyAuthor.post(`modern_authoring/delete_image`, {
            body: {
                image_key: imageKey,
            },
        });

        return response.success === true;
    } catch {
        throw new Error('Image deletion failed');
    }
};

/**
 * Handles image renaming in S3
 * @param ImageKey The key of the image
 * @param newImageName The new name for the image that will go into the metadata.
 * @returns Promise resolving to the image key, last modified date, and metadata
 */
export const renameImageInS3 = async (imageKey: string, newImageName: string): Promise<S3Object> => {
    try {
        const response = await zyAuthor.post(`modern_authoring/rename_image`, {
            body: {
                image_key: imageKey,
                new_image_name: newImageName,
            },
        });

        return response.image_key_and_last_modified_and_metadata;
    } catch {
        throw new Error('Image rename failed');
    }
};

/**
 * Persists `MaDocuments` metadata and batch-imports into TipTap Cloud.
 *
 * @param documents
 * @param options.overwrite_existing - When true, existing `document_name` rows are skipped in DB.
 * @returns success status.
 */
export async function importMaDocuments(
    documents: MaDocumentImportPayload[],
    options = { overwriteExisting: false, noShareExisting: false },
) {
    return zyAuthor.post<{
        success: boolean;
        documents: { document_name: string; ma_document_id: number; ma_zybook_chapter_id?: number | null }[];
    }>('modern_authoring/import_documents', {
        body: {
            documents,
            overwrite_existing: options.overwriteExisting,
            no_share_existing: options.noShareExisting,
        },
    });
}

/**
 * Deletes `MaDocuments` rows whose `document_name` matches the given `name` values, and optionally deletes
 * the same document names from TipTap Cloud.
 *
 * @param documents - Each `name` is the TipTap / `MaDocuments.document_name` identifier.
 * @param options.delete_tiptap - When true (default), also deletes each document on the TipTap document server.
 * @returns success status; when any TipTap deletion fails, `success` is false and `failed_documents` lists each failure.
 * Database rows are removed only for documents whose TipTap delete succeeded (`deleted_names`).
 */
export async function deleteMaDocuments(
    documents: { name: string }[],
    options: { delete_tiptap?: boolean } = {},
): Promise<{
    success: boolean;
    deleted_names?: string[];
    failed_documents?: { name: string; error: string }[];
}> {
    const { delete_tiptap = true } = options;
    return zyAuthor.post('modern_authoring/delete_documents', {
        body: {
            documents,
            delete_tiptap,
        },
    });
}

/**
 * Lists modern authoring documents for a zybook id.
 *
 * @param zybookId - zyBooks.zybook_id.
 * @param options.releaseId - optional published release-number filter. When omitted, returns dev documents whose release_id is null.
 * @returns documents rows for the given zybook.
 */
export async function getMaDocumentsByZybook(zybookId: number, options?: { releaseId?: number }): Promise<GetMaDocumentsResponse[]> {
    const params = new URLSearchParams();
    if (options?.releaseId != null) {
        params.set('release_id', String(options.releaseId));
    }
    const paramsString = params.toString();

    const response = await zyAuthor.get(`modern_authoring/${zybookId}/documents${paramsString ? `?${paramsString}` : ''}`);
    if (!response.success) {
        throw new Error('Failed to fetch ma documents');
    }
    return response.documents;
}

/**
 * Lists modern authoring documents.
 *
 * @param options.limit - optional max rows (default 100)
 * @param options.offset - optional rows to skip (default 0)
 * @returns
 */
export async function getMaDocuments(options?: { limit?: number; offset?: number }): Promise<GetMaDocumentsResponse[]> {
    const params = new URLSearchParams();
    if (options?.limit != null) {
        params.set('limit', String(options.limit));
    }
    if (options?.offset != null) {
        params.set('offset', String(options.offset));
    }

    const response = await zyAuthor.get(`modern_authoring/documents${params.toString() ? `?${params.toString()}` : ''}`);
    if (!response.success) {
        throw new Error('Failed to fetch ma documents');
    }
    return response.documents;
}

/**
 * Fetches a modern authoring document by document id.
 *
 * @param documentId
 * @returns
 */
export async function getMaDocument(documentId: number): Promise<GetMaDocumentResponse> {
    const response = await zyAuthor.get(`modern_authoring/document/${documentId}`);
    if (!response.success) {
        throw new Error('Failed to fetch ma document');
    }
    return response.document;
}

export interface GetTiptapDocumentJsonOptions {
    /** Fragment name(s) to export. */
    fragment?: string | string[];
    /** Y.js document version to retrieve. Defaults to the latest version. */
    version?: string;
}

/**
 * Fetches a document's TipTap JSON from the TipTap document server (no collaboration).
 *
 * @param documentName - TipTap / `MaDocuments.document_name` identifier.
 * @param options - Optional `fragment` and `version` TipTap query params.
 */
export async function getTiptapDocumentJson(documentName: string, options?: GetTiptapDocumentJsonOptions): Promise<JSONContent> {
    const params = new URLSearchParams({ document_name: documentName });

    if (options?.version != null) {
        params.set('version', options.version);
    }

    if (options?.fragment != null) {
        const fragments = Array.isArray(options.fragment) ? options.fragment : [options.fragment];
        for (const fragment of fragments) {
            params.append('fragment', fragment);
        }
    }

    const response = await zyAuthor.get<{ success: boolean; document: JSONContent }>(`modern_authoring/get_document?${params.toString()}`);
    if (!response.success) {
        throw new Error('Failed to fetch document from TipTap server');
    }
    return response.document;
}

/**
 * Bulk-fetches TipTap JSON for multiple documents (no collaboration).
 *
 * @param documentNames - TipTap / `MaDocuments.document_name` identifiers.
 * @param options - Optional `fragment` and `version` TipTap query params.
 */
export async function getTiptapDocumentsJson(
    documentNames: string[],
    options?: GetTiptapDocumentJsonOptions,
): Promise<Record<string, JSONContent | null>> {
    const uniqueNames = [...new Set(documentNames)];
    if (uniqueNames.length === 0) {
        return {};
    }

    const TIPTAP_BULK_DOCUMENT_MAX = 100;
    if (uniqueNames.length > TIPTAP_BULK_DOCUMENT_MAX) {
        throw new Error(`Cannot fetch more than ${TIPTAP_BULK_DOCUMENT_MAX} TipTap documents at once.`);
    }

    const params = new URLSearchParams();
    if (options?.version != null) {
        params.set('version', options.version);
    }
    if (options?.fragment != null) {
        const fragments = Array.isArray(options.fragment) ? options.fragment : [options.fragment];
        for (const fragment of fragments) {
            params.append('fragment', fragment);
        }
    }
    const query = params.toString();
    const queryString = query ? `?${query}` : '';

    const response = await zyAuthor.post<{ success: boolean; documents: Record<string, JSONContent | null> }>(
        `modern_authoring/get_documents${queryString}`,
        {
            body: { documents: uniqueNames },
        },
    );
    if (!response.success) {
        throw new Error('Failed to fetch documents from TipTap server');
    }
    return response.documents;
}

/**
 * Converts a Google Doc’s zyXML export into JSON.
 *
 * @param gdocId - Google Doc id to convert.
 * @param devZybookCode - Dev zybook code paired with the doc’s content source to resolve the subject.
 * @param options.forceNoContentVersion - Whether to force parsing of gdoc with no content version.
 * @param options.returnContents - Whether to return the contents of the gdoc.
 * @returns ProseMirror JSON.
 */
export async function getConvertedGdocJson(gdocId: string, options?: GetConvertedGdocJsonOptions): Promise<GetConvertedGdocJsonResponse> {
    const { forceNoContentVersion = true, returnContents = true, contentVersion = null } = options || {};
    return zyAuthor.post<GetConvertedGdocJsonResponse>(`modern_authoring/gdocId_to_json`, {
        body: {
            gdoc_id: gdocId,
            content_version: contentVersion,
            force_no_content_version: forceNoContentVersion,
            return_contents: returnContents,
        },
        maxAttempts: 300,
    });
}

/**
 * Fetches import logs for a zybook.
 *
 * @param zybookId - zyBooks.zybook_id whose log rows should be fetched.
 * @returns logs rows for the given zybook.
 */
export async function getMaDocumentImportLogs(zybookId: number): Promise<MaDocumentImportLog[]> {
    const data = await zyAuthor.get<{ success?: boolean; logs?: MaDocumentImportLog[] }>(`modern_authoring/${zybookId}/ma_import_logs`);
    if (!data.success) {
        throw new Error('Failed to fetch ma document import logs');
    }
    return data.logs ?? [];
}

/**
 * Loads the subject for a zybook.
 *
 * @param zybookId
 * @returns `ZyServerSubject` for the resolved subject.
 */
/**
 * Valid zyVersion tokens for a document, unioned across every zyBook the document is placed on.
 */
export async function getDocumentZyVersions(maDocumentId: number): Promise<Pick<GetDocumentZyVersionsResponse, 'versions'>> {
    const response = await zyAuthor.get<GetDocumentZyVersionsResponse>(`modern_authoring/documents/${maDocumentId}/zy_versions`);
    if (!response.success) {
        throw new Error('Failed to fetch zy versions for document');
    }
    return {
        versions: response.versions,
    };
}

export async function getZybookSubject(zybookId: number): Promise<ZyServerSubject> {
    const response = await zyAuthor.get<{ success: boolean; subject: ZyServerSubject }>(`subjects/${zybookId}`);
    if (!response.success) {
        throw new Error('Failed to fetch subject');
    }
    return response.subject;
}

/**
 * Loads a subject by subject_id.
 *
 * @param subjectId - Subjects.subject_id
 * @returns Full `ZyServerSubject` metadata from zyauthor.
 */
export async function getSubject(subjectId: number): Promise<ZyServerSubject> {
    const response = await zyAuthor.get<{ success: boolean; subject: ZyServerSubject }>(`subjects/id/${subjectId}`);
    if (!response.success) {
        throw new Error('Failed to fetch subject');
    }
    return response.subject;
}

/**
 * Lists all subjects from zyauthor.
 */
export async function getAllSubjects(): Promise<MaSubjectSummary[]> {
    const response = await zyAuthor.get<{
        success: boolean;
        subjects: MaSubjectSummary[];
    }>('modern_authoring/subjects');
    if (!response.success) {
        throw new Error('Failed to fetch subjects');
    }
    return response.subjects.map(subject => ({
        subject_id: subject.subject_id,
        name: subject.name,
        cover_color: subject.cover_color ?? null,
    }));
}

/**
 * Lists dev and release zyBooks for a subject, grouped by variant, with MaZybookDocuments membership.
 *
 * @param subjectId - Subjects.subject_id
 */
export async function getSubjectBooks(subjectId: number): Promise<Omit<GetSubjectBooksResponse, 'success'>> {
    const response = await zyAuthor.get<GetSubjectBooksResponse>(`modern_authoring/subjects/id/${subjectId}/books`);
    if (!response.success) {
        throw new Error('Failed to fetch subject books');
    }
    return {
        subject: response.subject,
        variants: response.variants,
        dev_books: response.dev_books,
        release_rows: response.release_rows,
    };
}

/**
 * Lists all development zyBooks linked to a subject (primary dev book, lab dev book, and additional dev books).
 *
 * @param subjectId - Subjects.subject_id
 */
export async function getSubjectDevZybooks(subjectId: number): Promise<SubjectDevZybook[]> {
    const response = await zyAuthor.get<GetSubjectDevZybooksResponse>(`subjects/id/${subjectId}/dev_zybooks`);
    if (!response.success) {
        throw new Error('Failed to fetch subject dev zybooks');
    }
    return response.zybooks;
}

/**
 * Lists all test questions for subjects with the same content source as the given subject.
 * Only test questions from each subject's latest release are returned.
 *
 * @param subjectId - Subjects.subject_id
 * @returns Test questions with chapter/section placement from each subject's latest release zybook,
 *          and the count of subjects sharing that content source.
 */
export async function getSameContentSourceTestQuestions(
    subjectId: number,
): Promise<Pick<GetSameContentSourceTestQuestionsResponse, 'test_questions' | 'same_content_source_subject_count'>> {
    const response = await zyAuthor.get<GetSameContentSourceTestQuestionsResponse>(
        `modern_authoring/subjects/id/${subjectId}/same_content_source_test_questions`,
    );
    if (!response.success) {
        throw new Error('Failed to fetch same content source test questions');
    }
    return {
        test_questions: response.test_questions,
        same_content_source_subject_count: response.same_content_source_subject_count,
    };
}

/**
 * Deletes every `MaDocumentImportLogs` row for the given zybook id.
 *
 * @param zybookId - zyBooks.zybook_id whose log rows should be removed.
 * @returns success status.
 */
export async function deleteMaDocumentImportLogs(zybookId: number): Promise<{ success: boolean }> {
    return zyAuthor.delete(`modern_authoring/${zybookId}/ma_import_logs/delete`);
}

/**
 * Inserts import logs into `MaDocumentImportLogs`.
 *
 * @param logs - Batch of log rows.
 * @returns success status.
 */
export async function createMaDocumentImportLogs(logs: MaDocumentImportLog[]): Promise<{ success: boolean }> {
    return zyAuthor.post('modern_authoring/import_logs', {
        body: { logs },
    });
}

/**
 * Lists work status rows for a modern authoring document.
 *
 * @param documentId - MaDocuments.ma_document_id.
 * @param options.userId - Optional filter by user id.
 */
export async function getMaDocumentWorkStatuses(documentId: number, options?: { userId?: number }): Promise<MaDocumentWorkStatus[]> {
    const params = new URLSearchParams();
    if (options?.userId != null) {
        params.set('user_id', String(options.userId));
    }
    const paramsString = params.toString();
    const response = await zyAuthor.get<{ success: boolean; work_statuses?: MaDocumentWorkStatus[] }>(
        `modern_authoring/document/${documentId}/work_status${paramsString ? `?${paramsString}` : ''}`,
    );
    if (!response.success) {
        throw new Error('Failed to fetch ma document work statuses');
    }
    return response.work_statuses ?? [];
}

/** Starts a work session for the signed-in user. */
export async function startMaDocumentWorkStatus(
    documentId: number,
    payload: { startStatus: MaDocumentWorkStatusValue; userId: number },
): Promise<MaDocumentWorkStatus> {
    const { workStatus } = await postMaDocumentWorkStatus(documentId, { start_status: payload.startStatus, user_id: payload.userId });
    return workStatus;
}

export type MaWorkNotifyPayload = {
    userId: number;
    zybookCode: string;
};

/**
 * Ends the signed-in user's open work session.
 *
 * @param payload.endStatus - Must be one of the options for the session's start status.
 * @param payload.notify - Emails this author a link to the section in `zybookCode`.
 */
export type MaDocumentWorkStatusResult = {
    workStatus: MaDocumentWorkStatus;
    emailFailed: boolean;
};

export async function endMaDocumentWorkStatus(
    documentId: number,
    payload: {
        endStatus: MaDocumentWorkEndStatusValue;
        userId: number;
        workDuration: number;
        remark: string | null;
        notify?: MaWorkNotifyPayload;
    },
): Promise<MaDocumentWorkStatusResult> {
    return postMaDocumentWorkStatus(documentId, {
        end_status: payload.endStatus,
        user_id: payload.userId,
        work_duration: payload.workDuration,
        remark: payload.remark,
        ...(payload.notify ? { notify_user_id: payload.notify.userId, notify_zybook_code: payload.notify.zybookCode } : {}),
    });
}

async function postMaDocumentWorkStatus(documentId: number, body: Record<string, unknown>): Promise<MaDocumentWorkStatusResult> {
    const response = await zyAuthor.post<{ success: boolean; work_status?: MaDocumentWorkStatus; email_failed?: boolean }>(
        `modern_authoring/document/${documentId}/work_status`,
        { body },
    );
    if (!response.success || !response.work_status) {
        throw new Error('Failed to save ma document work status');
    }
    return { workStatus: response.work_status, emailFailed: response.email_failed === true };
}

export type MaWorkStatusEdit = {
    startStatus?: MaDocumentWorkStatusValue;
    endStatus?: MaDocumentWorkEndStatusValue;
    workDuration?: number;
    remark?: string | null;
};

/**
 * Corrects any author's work session. The server keeps the end status one of the start status's options and only
 * lets ended sessions change their end status or duration.
 */
export async function editMaDocumentWorkStatus(
    documentId: number,
    workStatusId: number,
    edit: MaWorkStatusEdit,
): Promise<MaDocumentWorkStatus> {
    const body: Record<string, unknown> = {};
    if (edit.startStatus !== undefined) {
        body.start_status = edit.startStatus;
    }
    if (edit.endStatus !== undefined) {
        body.end_status = edit.endStatus;
    }
    if (edit.workDuration !== undefined) {
        body.work_duration = edit.workDuration;
    }
    if (edit.remark !== undefined) {
        body.remark = edit.remark;
    }
    const response = await zyAuthor.patch<{ success: boolean; work_status?: MaDocumentWorkStatus }>(
        `modern_authoring/document/${documentId}/work_status/${workStatusId}`,
        { body },
    );
    if (!response.success || !response.work_status) {
        throw new Error('Failed to update the work session');
    }
    return response.work_status;
}

export interface MaHandoffRecipient {
    user_id: number;
    first_name: string | null;
    last_name: string | null;
}

/**
 * Emails the people a new comment or reply mentions and, for replies, the thread's other participants.
 * Comments live in the collaboration service, so the caller says who is involved; the server limits
 * recipients to authors and super users. Resolves to the number of emails sent.
 */
export async function sendMaCommentNotifications(
    documentId: number,
    payload: {
        zybookCode: string;
        threadId: string;
        commentText: string;
        isReply: boolean;
        mentionedUserIds: number[];
        participantUserIds: number[];
    },
): Promise<number> {
    const response = await zyAuthor.post<{ success: boolean; notified?: number }>(
        `modern_authoring/document/${documentId}/comment_notifications`,
        {
            body: {
                zybook_code: payload.zybookCode,
                thread_id: payload.threadId,
                comment_text: payload.commentText,
                is_reply: payload.isReply,
                mentioned_user_ids: payload.mentionedUserIds,
                participant_user_ids: payload.participantUserIds,
            },
        },
    );
    if (!response.success) {
        throw new Error('Failed to send comment notifications');
    }
    return response.notified ?? 0;
}

/** Authors and super users the signed-in user can hand work off to or @mention, including themselves. */
export async function getMaHandoffRecipients(): Promise<MaHandoffRecipient[]> {
    const response = await zyAuthor.get<{ success: boolean; recipients?: MaHandoffRecipient[] }>('modern_authoring/handoff_recipients');
    if (!response.success) {
        throw new Error('Failed to fetch notify recipients');
    }
    return response.recipients ?? [];
}

/**
 * Creates a new Gdoc Drawing
 * @param subject - The subject associated with the drawing
 * @param releaseID - The release ID associated with the drawing
 * @returns Promise resolving to the drawing URL
 */
export const createGdocDrawing = async (subject: string, releaseID: string): Promise<string> => {
    const shared_drive_id = '0ANdwUSyXXuW3Uk9PVA';
    try {
        const response = await zyAuthor.post(`modern_authoring/create_gdoc_drawing_in_folder`, {
            body: {
                shared_drive_id: shared_drive_id,
                subject: subject,
                releaseID: releaseID,
            },
        });
        return response.file_id;
    } catch {
        throw new Error('Failed to create Gdoc Drawing');
    }
};

export const uploadGdocDrawingToS3: ZybooksApi['uploadGdocDrawingToS3'] = (...args) => browserApi().uploadGdocDrawingToS3(...args);

interface ConvertProseMirrorToZybooksJsonServerResponse {
    contentResources: unknown[];
    enumMaps: Partial<ReferenceToContentResources>;
}

/**
 * Converts ProseMirror JSON to zyBooks content resources via zyauthor (Lambda).
 * Drop-in replacement for {@link convertProseMirrorToZybooksJSON} from `to-zybooks-json.ts`.
 */
export async function convertProseMirrorToZybooksJSONServer(
    prosemirrorDoc: JSONContent | string,
    options?: ConvertProseMirrorToZybooksJSONOptions,
): Promise<{ contentResources: any[]; enumMaps: Partial<ReferenceToContentResources> }> {
    const response = await zyAuthor.post<ConvertProseMirrorToZybooksJsonServerResponse>('modern_authoring/prosemirror_to_zybooks_json', {
        body: {
            prosemirrorDoc,
            options,
        },
    });
    return { contentResources: response.contentResources, enumMaps: response.enumMaps };
}

/**
 * Creates an empty development zybook and registers it as additional dev content on the subject.
 *
 * @param subjectId - Subject to associate the new dev zybook with.
 * @param newZybookCode - Unique code for the new development zybook.
 * @param title - Display title for the new development zybook.
 */
export async function createDevZybook(subjectId: number, newZybookCode: string, title: string): Promise<CreateDevZybookResponse> {
    return zyAuthor.post<CreateDevZybookResponse>('modern_authoring/create_dev_zybook', {
        body: {
            subject_id: subjectId,
            new_zybook_code: newZybookCode,
            title,
        },
    });
}

/**
 * Fetches the about pages for a zybook
 * @param zybookCode - The code of the zybook to fetch the about pages for
 * @returns Promise resolving to an array of ZyServerSection objects
 */
export async function getZybookAboutPages(zybookCode: string): Promise<ZyServerSection[]> {
    const response: GetZybookAboutPagesResponse = await zyServer.get(`zybook/${encodeURIComponent(zybookCode)}/about`);
    if (!response.success) {
        throw new Error('Failed to fetch zybook about pages');
    }
    return response.about_pages;
}

/**
 * Links an image to a document in modern authoring
 * @param maImageId - The ID of the image to link
 * @param maDocumentId - The ID of the document to link the image to
 * @returns Promise resolving to the linked image document
 */
export async function linkImageToDocument(maImageId: number, maDocumentId: number): Promise<MaImageDocument> {
    const response = await zyAuthor.post<LinkImageResponse>(`modern_authoring/link_image`, {
        body: {
            ma_image_id: maImageId,
            ma_document_id: maDocumentId,
        },
    });
    if (!response.success) {
        throw new Error(response.error ?? 'Failed to link image to document');
    }
    return response.ma_image_document;
}

/**
 * Links an existing MA library image to a document using a pasted library image URL.
 */
export async function linkLibraryImageFromSrc(
    maDocumentId: number,
    src: string,
): Promise<{ ma_image: MaImage; ma_image_document: MaImageDocument }> {
    const response = await zyAuthor.post<UploadedImageResponse>('modern_authoring/link_library_image_from_src', {
        body: {
            ma_document_id: maDocumentId,
            src,
        },
    });
    if (!response.success || !response.ma_image || !response.ma_image_document) {
        throw new Error(response.error ?? 'Failed to link library image from URL');
    }
    return { ma_image: response.ma_image, ma_image_document: response.ma_image_document };
}

/**
 * Lists MA images from the modern authoring image library.
 */
export async function getMaImageLibrarySubjects(): Promise<string[]> {
    const response = await zyAuthor.get<GetMaImageLibrarySubjectsResponse>('modern_authoring/get_image_library_subjects');
    if (!response.success) {
        throw new Error(response.error ?? 'Failed to fetch image library subjects');
    }
    return response.subjects;
}

/**
 * Lists MA images from the modern authoring image library.
 */
export async function getMaImages(options?: {
    limit?: number;
    offset?: number;
    search?: string;
    sortBy?: 'date' | 'name';
    isDescending?: boolean;
    subjects?: string[];
    excludeUnused?: boolean;
    uploadedByMe?: boolean;
}): Promise<GetMaImagesResponse> {
    const params = new URLSearchParams();
    if (options?.limit != null) {
        params.set('limit', String(options.limit));
    }
    if (options?.offset != null) {
        params.set('offset', String(options.offset));
    }
    if (options?.search) {
        params.set('search', options.search);
    }
    if (options?.sortBy) {
        params.set('sort_by', options.sortBy);
    }
    params.set('sort_order', options?.isDescending === false ? 'asc' : 'desc');
    options?.subjects?.forEach(subject => {
        params.append('subjects', subject);
    });
    if (options?.excludeUnused) {
        params.set('exclude_unused', 'true');
    }
    if (options?.uploadedByMe) {
        params.set('uploaded_by_me', 'true');
    }

    const response = await zyAuthor.get<GetMaImagesResponse>(
        `modern_authoring/get_images${params.toString() ? `?${params.toString()}` : ''}`,
    );
    if (!response.success) {
        throw new Error(response.error ?? 'Failed to fetch ma images');
    }
    return response;
}

export interface UpdateMaImageAltTextResponse {
    success: boolean;
    ma_image: MaImage;
    error?: string;
}

export interface GetMaImageAltTextsResponse {
    success: boolean;
    alt_texts: Record<string, string>;
    error?: string;
}

/**
 * Lists documents and zyBook placements where an MA library image is linked.
 */
export async function getMaImageUsages(maImageId: number): Promise<MaImageUsage[]> {
    const response = await zyAuthor.get<GetMaImageUsagesResponse>(`modern_authoring/get_ma_image_usages?ma_image_id=${maImageId}`);
    if (!response.success) {
        throw new Error(response.error ?? 'Failed to fetch image usages');
    }
    return response.usages;
}

/**
 * Returns non-empty MaImages.alt_text values for the requested image IDs.
 */
export async function getMaImageAltTexts(maImageIds: number[]): Promise<Record<number, string>> {
    if (maImageIds.length === 0) {
        return {};
    }

    const response = await zyAuthor.post<GetMaImageAltTextsResponse>('modern_authoring/get_ma_image_alt_texts', {
        body: {
            ma_image_ids: maImageIds,
        },
    });
    if (!response.success) {
        throw new Error(response.error ?? 'Failed to fetch image alt texts');
    }

    const altTexts: Record<number, string> = {};
    for (const [key, value] of Object.entries(response.alt_texts ?? {})) {
        const maImageId = Number(key);
        if (Number.isInteger(maImageId) && maImageId > 0 && value) {
            altTexts[maImageId] = value;
        }
    }
    return altTexts;
}

/**
 * Sets MaImages.alt_text on the shared library record, overwriting any existing value.
 *
 * @param maImageId - The ID of the image to update.
 * @param altText - The new alt text for the image.
 * @returns Promise resolving to the updated image.
 */
export async function updateMaImageAltText(maImageId: number, altText: string): Promise<MaImage> {
    const response = await zyAuthor.post<UpdateMaImageAltTextResponse>('modern_authoring/update_image_alt_text', {
        body: {
            ma_image_id: maImageId,
            alt_text: altText,
        },
    });
    if (!response.success) {
        throw new Error(response.error ?? 'Failed to update image alt text');
    }
    return response.ma_image;
}

/**
 * Removes the MaImageDocuments link when an editor image node is deleted.
 *
 * @param maImageNodeUuid - The UUID of the editor image node to unlink.
 * @param maDocumentId - The ID of the document to unlink the image from.
 * @returns Promise resolving to void.
 */
export async function unlinkImageFromDocument(maImageNodeUuid: string, maDocumentId: number): Promise<void> {
    const response = await zyAuthor.post<{ success: boolean; error?: string }>('modern_authoring/unlink_image', {
        body: {
            ma_image_node_uuid: maImageNodeUuid,
            ma_document_id: maDocumentId,
        },
    });
    if (!response.success) {
        throw new Error(response.error ?? 'Failed to unlink image from document');
    }
}

export async function registerDocumentImages(
    maDocumentId: number,
    subject: string,
    releaseId: string | number,
    images: Array<{ image_uuid: string; filename?: string }>,
): Promise<RegisteredDocumentImage[]> {
    const response = await zyAuthor.post<{ success: boolean; images: RegisteredDocumentImage[]; error?: string }>(
        'modern_authoring/register_document_images',
        {
            body: {
                ma_document_id: maDocumentId,
                subject,
                release_id: releaseId,
                images,
            },
        },
    );
    if (!response.success) {
        throw new Error(response.error ?? 'Failed to register document images');
    }
    return response.images;
}

export async function materializeDocumentImages(maDocumentId: number, subject: string, releaseId: string | number): Promise<void> {
    const response = await zyAuthor.post<{ success: boolean; error?: string }>('modern_authoring/materialize_document_images', {
        body: {
            ma_document_id: maDocumentId,
            subject,
            release_id: releaseId,
        },
    });
    if (!response.success) {
        throw new Error(response.error ?? 'Failed to materialize document images');
    }
}

/**
 * Creates or updates an orphan itlab ContentResource for MA VM preview.
 */
export async function upsertItLabContentResource(params: {
    zybookId: number;
    guid: string;
    caption: string;
    instructions: string;
    parts: number;
    payload: Record<string, unknown>;
}): Promise<number> {
    const response = await zyAuthor.post<UpsertItLabContentResourceResponse>('modern_authoring/itlab_content_resource', {
        body: {
            zybook_id: params.zybookId,
            guid: params.guid,
            caption: params.caption,
            instructions: params.instructions,
            parts: params.parts,
            payload: params.payload,
        },
    });
    return response.content_resource_id;
}

/**
 * Fetches available IT Lab VM images from itlabs-server.
 */
export async function getItLabImages(): Promise<GetItLabImagesResponse> {
    return itLabsServer.get('images');
}

/**
 * Creates IT Lab VM instances and returns streaming URLs.
 * @param zybookCode - The zybook code for the lab
 * @param contentResourceId - The content resource ID for the lab
 * @param options - Optional settings (remoteGrading defaults to true)
 */
export async function createItLabInstances(
    zybookCode: string,
    contentResourceId: number,
    options?: { remoteGrading?: boolean },
): Promise<CreateItLabInstancesResponse> {
    return itLabsServer.post(`zybook/${encodeURIComponent(zybookCode)}/content_resource/${contentResourceId}`, {
        body: { remote_grading: options?.remoteGrading ?? true },
    });
}

/**
 * Deletes IT Lab VM instances for a content resource.
 * @param zybookCode - The zybook code for the lab
 * @param contentResourceId - The content resource ID for the lab
 */
export async function deleteItLabInstances(zybookCode: string, contentResourceId: number): Promise<DeleteItLabInstancesResponse> {
    return itLabsServer.delete(`zybook/${encodeURIComponent(zybookCode)}/content_resource/${contentResourceId}`);
}
